import React, { ReactNode, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { registerAuthFunctions, unregisterAuthFunctions } from "@/lib/auth";

import LoadingSpinner from "./LoadingSpinner";

interface AuthProviderProps {
  children: ReactNode;
}

const isLoginRequiredError = (error: Error): boolean =>
  error.message === "Login required" || (error as any).error === "login_required";

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout, loginWithRedirect, error } =
    useAuth0();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!error) return;
    if (isLoginRequiredError(error)) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [error, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    registerAuthFunctions(getAccessTokenSilently, logout);

    let cancelled = false;
    (async () => {
      try {
        await getAccessTokenSilently();
        if (!cancelled) setIsReady(true);
      } catch (e) {
        if (!cancelled) {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    })();

    return () => {
      cancelled = true;
      unregisterAuthFunctions();
    };
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading || (isAuthenticated && !isReady)) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

export default AuthProvider;
